import type { ContentField, ContentNode } from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";

function isGridComplete(field: Extract<ContentField, { type: "grid" }>, value: unknown): boolean {
  const gridData = (value as Record<string, Record<string, string>> | undefined) ?? {};
  for (const row of field.rows) {
    for (const col of field.columns) {
      const cellVal = gridData[row.label]?.[col] ?? row.defaults?.[col] ?? "";
      if (cellVal === "") {
        return false;
      }
    }
  }
  return true;
}

function isSubTemplateComplete(subTemplateData: SubTemplateData | undefined): boolean {
  if (!subTemplateData) {
    return false;
  }
  const { completed, total } = getContentCompletion(
    subTemplateData.content,
    subTemplateData.sectionToggles,
    subTemplateData.values,
    subTemplateData.subTemplateValues,
  );
  return total === 0 || completed === total;
}

export function isFieldComplete(
  field: ContentField,
  value?: unknown,
  subTemplateData?: SubTemplateData,
): boolean {
  switch (field.type) {
    case "text":
    case "date": {
      if (field.optional) {
        return true;
      }
      return typeof value === "string" && value.trim() !== "";
    }

    case "select": {
      return typeof value === "string" && value !== "";
    }

    case "checkbox-list": {
      return true;
    } // 0 selections is valid

    case "statement-list": {
      const items = value as { text: string; checked: boolean }[] | undefined;
      return Array.isArray(items) && items.some((i) => i.checked);
    }

    case "score": {
      return value !== undefined && value !== null;
    }

    case "grid": {
      return isGridComplete(field, value);
    }

    case "sub-template": {
      return isSubTemplateComplete(subTemplateData);
    }

    default: {
      return true;
    }
  }
}

export function getContentCompletion(
  content: ContentNode[],
  sectionToggles: Record<string, boolean>,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
): { completed: number; total: number } {
  let completed = 0;
  let total = 0;
  let currentSectionVisible = true;

  for (const node of content) {
    if (node.type === "section") {
      currentSectionVisible = sectionToggles[node.id] ?? true;
    } else if (node.type === "field") {
      if (!currentSectionVisible) {
        continue;
      }
      const { field } = node;
      total++;
      if (isFieldComplete(field, values[field.id], subTemplateValues[field.id])) {
        completed++;
      }
    }
  }

  return { completed, total };
}
